import { FC, useEffect, useId, useState } from 'react';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { useApi, fetchApiRef } from '@backstage/core-plugin-api';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import FormHelperText from '@material-ui/core/FormHelperText';
import CircularProgress from '@material-ui/core/CircularProgress';
import { load as parseYaml } from 'js-yaml';
import { resolveOwnerRepo } from '../../services/githubService';

const PROXY_BASE = '/api/proxy';

export const GithubWorkflowInputPicker: FC<FieldExtensionComponentProps<string>> = ({
  formData,
  onChange,
  formContext,
  uiSchema,
  schema,
  required,
}) => {
  const fetchApi = useApi(fetchApiRef);
  const [options, setOptions] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const labelId = useId();

  const repoFieldName = uiSchema?.['ui:options']?.repoField as string | undefined;
  const staticRepo = uiSchema?.['ui:options']?.repo as string | undefined;
  const branchFieldName = uiSchema?.['ui:options']?.branchField as string | undefined;
  const staticBranch = uiSchema?.['ui:options']?.branch as string | undefined;
  const workflow = (uiSchema?.['ui:options']?.workflow as string | undefined) ?? 'publish-tenant.yaml';
  const inputName = (uiSchema?.['ui:options']?.inputName as string | undefined) ?? 'tenant';

  const formDataObj = formContext?.formData as Record<string, unknown> | undefined;
  const repoName = staticRepo ?? (repoFieldName ? (formDataObj?.[repoFieldName] as string | undefined) : undefined);
  const branch = staticBranch ?? (branchFieldName ? (formDataObj?.[branchFieldName] as string | undefined) : undefined);

  // Build owner/repo — repoName may be bare ("my-repo") or full ("PhysicsXLtd/my-repo")
  const ownerRepo = repoName ? resolveOwnerRepo(repoName) : undefined;

  useEffect(() => {
    if (!ownerRepo) {
      setOptions([]);
      return;
    }

    const load = async () => {
      setLoading(true);
      setError(undefined);
      try {
        const ref = branch ? `?ref=${encodeURIComponent(branch)}` : '';
        const response = await fetchApi.fetch(
          `${PROXY_BASE}/github/api/repos/${ownerRepo}/contents/.github/workflows/${workflow}${ref}`,
        );
        if (response.status === 404) throw new Error(`Branch does not contain \`${workflow}\``);
        if (!response.ok) throw new Error(`Could not fetch workflow: ${response.status}`);

        const json = await response.json();
        // GitHub contents API returns base64-encoded content
        const content = atob(json.content.replace(/\n/g, ''));
        const parsed = parseYaml(content) as any;

        const workflowOptions: string[] = (
          parsed?.on?.workflow_dispatch?.inputs?.[inputName]?.options ?? []
        ).filter((opt: string, i: number, arr: string[]) => arr.indexOf(opt) === i);

        if (workflowOptions.length === 0) {
          setError(`No options found for input "${inputName}" in ${workflow}`);
        }
        setOptions(workflowOptions);
      } catch (e: any) {
        setError(e.message);
        setOptions([]);
      }
      setLoading(false);
    };

    load();
  }, [ownerRepo, branch, workflow, inputName, fetchApi]);

  const label = schema.title || inputName;
  const helperText = error
    ?? (!ownerRepo ? 'Please select a repository first' : undefined)
    ?? (branchFieldName && !branch ? 'Please select a branch first' : undefined)
    ?? (uiSchema?.['ui:help'] as string | undefined)
    ?? undefined;

  return (
    <FormControl
      fullWidth
      margin="normal"
      variant="outlined"
      disabled={!ownerRepo || loading}
      required={required}
    >
      <InputLabel id={labelId}>{label}</InputLabel>
      <Select
        labelId={labelId}
        value={formData || ''}
        onChange={e => onChange(e.target.value as string)}
        label={label}
        endAdornment={
          loading ? (
            <CircularProgress size={20} style={{ marginRight: 24 }} />
          ) : null
        }
      >
        {options.map(opt => (
          <MenuItem key={opt} value={opt}>
            {opt}
          </MenuItem>
        ))}
      </Select>
      <FormHelperText error={!!error}>{helperText}</FormHelperText>
    </FormControl>
  );
};
